import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Zap,
  Sun,
  Moon,
  Type,
  Gauge,
  Eye,
  Timer,
  FileText,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();
  const { user, updateSettings } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    wpm: user?.settings?.wpm || 300,
    font_size: user?.settings?.font_size || 48,
    theme: user?.settings?.theme || 'dark',
    show_orp: user?.settings?.show_orp !== false,
    pause_on_punctuation: user?.settings?.pause_on_punctuation !== false,
    skip_articles: user?.settings?.skip_articles || false,
    focus_guide: user?.settings?.focus_guide || 'none'
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(settings);
      setTheme(settings.theme);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-xl">Settings</h1>
              <p className="text-sm text-muted-foreground">Customize your experience</p>
            </div>
          </div>
          
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-pill gap-2"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        {/* Reading Speed */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gauge className="w-5 h-5" />
                Default Reading Speed
              </CardTitle>
              <CardDescription>
                Set your default words per minute (WPM)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Speed</span>
                <span className="text-2xl font-bold text-primary" data-testid="default-wpm-value">
                  {settings.wpm} WPM
                </span>
              </div>
              <Slider
                value={[settings.wpm]}
                onValueChange={([v]) => updateSetting('wpm', v)}
                min={100}
                max={1000}
                step={50}
                data-testid="default-wpm-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Beginner (100)</span>
                <span>Expert (1000)</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {[150, 250, 350, 500, 700].map(preset => (
                  <Button
                    key={preset}
                    variant={settings.wpm === preset ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateSetting('wpm', preset)}
                    data-testid={`preset-wpm-${preset}`}
                  >
                    {preset}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Display Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                Display Settings
              </CardTitle>
              <CardDescription>
                Customize how text appears in the reader
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Font Size */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Font Size</Label>
                  <span className="text-sm font-medium">{settings.font_size}px</span>
                </div>
                <Slider
                  value={[settings.font_size]}
                  onValueChange={([v]) => updateSetting('font_size', v)}
                  min={24}
                  max={72}
                  step={4}
                  data-testid="font-size-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Small (24px)</span>
                  <span>Large (72px)</span>
                </div>
              </div>

              {/* Theme */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Theme</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose your preferred color scheme
                  </p>
                </div>
                <Select
                  value={settings.theme}
                  onValueChange={(v) => updateSetting('theme', v)}
                >
                  <SelectTrigger className="w-32" data-testid="theme-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">
                      <div className="flex items-center gap-2">
                        <Sun className="w-4 h-4" />
                        Light
                      </div>
                    </SelectItem>
                    <SelectItem value="dark">
                      <div className="flex items-center gap-2">
                        <Moon className="w-4 h-4" />
                        Dark
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Reading Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Reading Options
              </CardTitle>
              <CardDescription>
                Fine-tune your RSVP reading experience
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ORP Highlighting */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>ORP Highlighting</Label>
                  <p className="text-sm text-muted-foreground">
                    Highlight the optimal recognition point in each word
                  </p>
                </div>
                <Switch
                  checked={settings.show_orp}
                  onCheckedChange={(v) => updateSetting('show_orp', v)}
                  data-testid="orp-switch"
                />
              </div>

              {/* Punctuation Pauses */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Punctuation Pauses</Label>
                  <p className="text-sm text-muted-foreground">
                    Pause longer at commas, periods, and other punctuation
                  </p>
                </div>
                <Switch
                  checked={settings.pause_on_punctuation}
                  onCheckedChange={(v) => updateSetting('pause_on_punctuation', v)}
                  data-testid="punctuation-switch"
                />
              </div>

              {/* Skip Articles */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Quick Articles</Label>
                  <p className="text-sm text-muted-foreground">
                    Show "a", "an", "the" more briefly
                  </p>
                </div>
                <Switch
                  checked={settings.skip_articles}
                  onCheckedChange={(v) => updateSetting('skip_articles', v)}
                  data-testid="articles-switch"
                />
              </div>

              {/* Focus Guide */}
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Focus Guide</Label>
                  <p className="text-sm text-muted-foreground">
                    Visual guide to help focus on the word
                  </p>
                </div>
                <Select
                  value={settings.focus_guide}
                  onValueChange={(v) => updateSetting('focus_guide', v)}
                >
                  <SelectTrigger className="w-32" data-testid="focus-guide-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="line">Line</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Name</span>
                  <span className="font-medium">{user?.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-muted-foreground">Member Since</span>
                  <span className="font-medium">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
